import { forwardRef, memo, useImperativeHandle, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Div, Form, InputField, Modal, useForm, useTheme, type ModalRef } from "react-better-html";

import { modalMaxWidthSmall } from "../../constants/app";

import { type Workspace } from "../../types/workspace";

import { useReduxAction, useSelectedOrganization } from "../../utils/hooks";
import { useI18n } from "../../utils/i18n";

type WorkspaceModalProps = {
   editedWorkspace?: Workspace;
   onFinish?: () => void;
} & React.ComponentProps<typeof Modal>;

const WorkspaceModal = forwardRef<ModalRef, WorkspaceModalProps>(({ editedWorkspace, onFinish, ...props }, ref) => {
   const theme = useTheme();
   const { t } = useI18n();
   const navigate = useNavigate();
   const selectedOrganization = useSelectedOrganization();

   const modalRef = useRef<ModalRef>(null);

   const createWorkspace = useReduxAction((actions) => actions.workspaces.createWorkspace);
   const editWorkspace = useReduxAction((actions) => actions.workspaces.editWorkspace);

   const form = useForm({
      defaultValues: {
         name: editedWorkspace?.name ?? "",
         description: editedWorkspace?.description ?? "",
         color: editedWorkspace?.color ?? theme.colors.primary,
      },
      requiredFields: ["name", "color"],
      onSubmit: (values) => {
         if (editedWorkspace) {
            editWorkspace({
               id: editedWorkspace.id,
               data: {
                  name: values.name,
                  description: values.description,
                  color: values.color,
               },
               onSuccess: () => {
                  modalRef.current?.close();
                  onFinish?.();
               },
            });
         } else {
            createWorkspace({
               data: {
                  name: values.name,
                  description: values.description,
                  color: values.color,
               },
               onSuccess: (response) => {
                  navigate(`/workspaces/${response.id}`);
                  onFinish?.();
               },
            });
         }
      },
   });

   useImperativeHandle(ref, (): ModalRef => modalRef.current!, []);

   return (
      <Modal
         title={t((l) => l.components.workspaceModal.title[editedWorkspace ? "edit" : "create"])}
         maxWidth={modalMaxWidthSmall}
         {...props}
         ref={modalRef}
      >
         <Form
            form={form}
            gap={theme.styles.gap}
            submitButtonText={t((l) => l.common[editedWorkspace ? "save" : "create"])}
         >
            <Div.row alignItems="center" gap={theme.styles.gap}>
               <InputField
                  label={t((l) => l.common.name)}
                  infoText={
                     selectedOrganization?.type === "AGENCY"
                        ? t((l) => l.components.workspaceModal.form.nameTooltip)
                        : undefined
                  }
                  {...form.getInputFieldProps("name")}
               />
               <InputField.color width="auto" label={t((l) => l.common.color)} {...form.getInputFieldProps("color")} />
            </Div.row>

            <InputField.multiline label={t((l) => l.common.description)} {...form.getTextAreaProps("description")} />
         </Form>
      </Modal>
   );
});

export default memo(WorkspaceModal);
